import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DisplayPost, Post } from '../../../../shared/models/publication';
import { PostService as LegacyPostService } from '../../../../shared/services/post';
import { ToastrService } from '../../../../shared/services/utilities/toastr.service';
import { StringUtilities } from '../../../../utils/misc/string-utilities';
import { UserRole } from '@adeo/ngx-kozikaza-api';
import { PostService, PostType } from '@adeo/ngx-kozikaza-api/post';
import { ReportService } from '@adeo/ngx-kozikaza-api/report';
import { KazaService as LegacyKazaService } from '../../../../shared/services/features/kaza/kaza.service';
import { PostsFluxService } from '../../../../shared/components/posts-flux/posts-flux.service';
import { KzTooltipDirective } from '../../../../kz-ui/ui/tooltip/kz-tooltip.directive';
import { ModalService } from "../../../../utils/modals/modals.service";
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';
import { KzIconsRegistryService } from '../../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsPen } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-pen.icon';
import { kzIconsGear } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-gear.icon';
import { kzIconsTrashcan } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-trashcan.icon';
import { kzIconsAlertTriangle } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-alert-triangle.icon';
import { UserStoreService } from 'src/app/shared/services/state-management/user-store.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-post-options',
  templateUrl: './post-options.component.html',
  styleUrls: ['./post-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PostOptionsComponent implements OnDestroy {

  @Input() post: Post;
  @Input() public isAdmin = false;
  @Input() public userLoggedIsPostAuthor = false;
  public optionMenuDisplay = false;

  public blacklistSubmitted = false;
  public pinnedSubmitted = false;
  public deleteSubmitted = false;
  public reportSubmitted = false;
  public editSubmitted = false;
  @Output() hasBeenDeleted: EventEmitter<string> = new EventEmitter<string>();
  @Output() updatedPost: EventEmitter<DisplayPost> = new EventEmitter<DisplayPost>();

  @ViewChild(KzTooltipDirective) kzTooltipDirective: KzTooltipDirective;

  private _activity: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private _optionClosed: Subject<void> = new Subject();
  private subscriptions: Array<Subscription> = [];

  public isAllowed = false;
  public role = UserRole;

  public postTypeVideo = PostType.Video;
  private routeName = ''
  constructor(
    private readonly legacyPostService: LegacyPostService,
    private readonly postService: PostService,
    private readonly reportService: ReportService,
    private readonly kazaService: LegacyKazaService,
    private readonly toastr: ToastrService,
    private readonly _modalService: ModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly postsFluxService: PostsFluxService,
    private readonly kzIconsRegistryService: KzIconsRegistryService,
    private userStoreService:UserStoreService,
    private activatedRoute:ActivatedRoute
  ) {
    this.kzIconsRegistryService.registerIcons([
      kzIconsPen,
      kzIconsAlertTriangle,
      kzIconsTrashcan,
      kzIconsGear,
    ]);
    this.routeName = this.activatedRoute.snapshot.routeConfig?.path;
  }

  private handleTooltipClosed() {
      this.subscriptions.push(of(true).pipe(
        delay(500),
        switchMap(() => this.kzTooltipDirective.closed),
        takeUntil(this._optionClosed),
        tap(() => {
          this.optionMenuDisplay = false;
          this.cdr.detectChanges();
          this._optionClosed.next();
        })
      ).subscribe());
  }

  private handleActivity() {
    this.subscriptions.push(
      this._activity.asObservable().pipe(
        debounceTime(10000),
        takeUntil(this._optionClosed),
        tap(() => this.kzTooltipDirective.close()),
      ).subscribe());
  }

  ngOnDestroy() {
    this.subscriptions.map((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  public optionMenuHide() {
    if (this.kzTooltipDirective) {
      this.kzTooltipDirective.close();
    }
  }

  public optionMenuToggle() {
    this.optionMenuDisplay = !this.optionMenuDisplay ;
    if (this.optionMenuDisplay) {
      this.handleTooltipClosed();
      this.kzTooltipDirective.open();
      // close option menu after 10s without clicking on a menu option.
      this.handleActivity();
    } else {
      this._optionClosed.next();
      this.kzTooltipDirective.close();
    }
    this.cdr.detectChanges();
  }

  public toggleBlacklisted() {
    this._activity.next(true);
    this.blacklistSubmitted = true;
    this.cdr.detectChanges();
    this.legacyPostService.update(this.post['@id'], { blacklisted: this.post.blacklisted } as Post).pipe(
      catchError((err) => {
        this.toastr.error($localize`:@@toast.could_not_blacklist_post:Can not blacklist the publication`);
        return throwError(err);
      }),
      finalize(() => { this.blacklistSubmitted = false; this.cdr.detectChanges(); }),
      tap((data) => this.toastr.info(this.post.blacklisted ? $localize`:@@toast.post_blacklisted:The publication has been blacklisted` :
        $localize`:@@toast.post_unblacklisted:The publication has been removed from the blacklist`)),
    ).subscribe();
  }

  public togglePinned() {
    this._activity.next(true);
    this.pinnedSubmitted = true;
    this.cdr.detectChanges();
    this.legacyPostService.update(this.post['@id'], { pinned: this.post.pinned } as Post).pipe(
      catchError((err) => {
        this.toastr.error($localize`:@@toast.could_not_pin_post:Unable to pin the publication`);
        return throwError(err);
      }),
      finalize(() => { this.pinnedSubmitted = false; this.cdr.detectChanges(); }),
      tap((data) => this.toastr.info(this.post.pinned ? $localize`:@@toast.post_pinned:The publication has been pinned` :
        $localize`:@@toast.post_unpinned:The publication has been unpinned`)),
    ).subscribe();
  }

  public report() {
    this._activity.next(true);
    const postDesc = StringUtilities.shorten(this.post.description, 20, '...', true);
    this.subscriptions.push(this._modalService.openYesNoDialogOnClosed({
      title: $localize`:@@popin.yesNoDialog.title.reportPost:Report a post`,
      content: $localize`:@@popin.yesNoDialog.content.reportPost:Do you confirm the reporting of ${postDesc}:postDesc:?`
    }).pipe(
      switchMap(() => {
        this.reportSubmitted = true;
        this.cdr.detectChanges();
        return this.reportService.reportPost(this.post['@id']).pipe(
          catchError((err) => {
            this.toastr.error((err.error['@type'] === 'ConstraintViolationList') ?
              $localize`:@@toast.post_already_flagged:Post already flagged` :
              $localize`:@@toast.error_with_description:An error has occurred \: ${err.error['hydra:description']}:description:`);
            this.reportSubmitted = false;
            this.optionMenuToggle();
            return throwError(err);
          }),
          tap(() => {
            this.toastr.info($localize`:@@toast.post_flagged:The publication has been reported`);
            this.reportSubmitted = false;
            this.optionMenuToggle();
          }),
        );
      })
    ).subscribe());
  }

  public edit() {
    this.optionMenuToggle();
    this.postsFluxService.editPost(this.post).pipe(
      tap((post) => {
        this.updatedPost.emit(post);
        this.editSubmitted = false;
        this.cdr.detectChanges();
      })
    ).subscribe();
  }

  public delete() {
    this._activity.next(true);
    const postDesc = StringUtilities.shorten(this.post.description, 20, '...', true);
    this.subscriptions.push(this._modalService.openYesNoDialogOnClosed({
      title: $localize`:@@popin.yesNoDialog.title.deletePost:Delete a post`,
      content: $localize`:@@popin.yesNoDialog.content.deletePost:Do you confirm the deletion of ${postDesc}:postDesc:?`
    }).pipe(
      tap(() => this.deleteSubmitted = true),
      switchMap(() =>
        this.postService.deletePost(this.post['@id']).pipe(
          catchError((err) => {
            this.deleteSubmitted = false;
            this.optionMenuToggle();
            this.toastr.error($localize`:@@toast.error_with_description:An error has occurred \: ${err.error['hydra:description']}:description:`);
            return throwError(err);
          }),
          tap(() => {
            this.hasBeenDeleted.emit(this.post['@id']);
            this.routeName === 'story' && this.userStoreService.getUserInfo();
            this.deleteSubmitted = false;
            this.optionMenuToggle();
            this.toastr.info($localize`:@@toast.post_deleted:The publication has been removed`);
          }),
        )
      )
    ).subscribe());
  }

}
